import { useParams, Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { ArrowLeft, CheckCircle2, Circle, BookOpen } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { getModuleById, getModuleProgress } from '@/lib/curriculum';
import { useProgress } from '@/hooks/useProgress';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

const ModulePage = () => {
  const { moduleId } = useParams<{ moduleId: string }>();
  const mod = getModuleById(moduleId || '');
  const { progress, isLessonComplete } = useProgress();

  useSeoMeta({
    title: mod ? `M${mod.number}: ${mod.title} — Reality Architect` : 'Module — Reality Architect',
    description: mod?.theme,
  });

  if (!mod) {
    return (
      <div className="container py-16 text-center">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Module not found</h1>
        <Link to="/" className="mt-4 inline-flex items-center gap-2 text-purple-600 hover:underline">
          <ArrowLeft className="size-4" />
          Back to Dashboard
        </Link>
      </div>
    );
  }

  const mp = getModuleProgress(mod.id, progress);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Module Header */}
      <div className="border-b bg-white dark:bg-slate-900">
        <div className="container py-8 md:py-12">
          <Link
            to="/"
            className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-500 transition hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            <ArrowLeft className="size-3.5" />
            All Modules
          </Link>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3">
                <span
                  className="inline-flex size-10 items-center justify-center rounded-lg text-white"
                  style={{ backgroundColor: mod.color }}
                >
                  <BookOpen className="size-5" />
                </span>
                <Badge variant="secondary" className="font-mono">
                  MODULE {mod.number}
                </Badge>
              </div>
              <h1 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 dark:text-white md:text-3xl">
                {mod.title}
              </h1>
              <p className="mt-2 text-slate-600 dark:text-slate-400">
                {mod.theme}
              </p>
            </div>
            <div className="shrink-0">
              <Card className="w-full sm:w-48">
                <CardContent className="px-4 py-3">
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Module Progress
                  </p>
                  <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900 dark:text-white">
                    {mp.percentage}%
                  </p>
                  <Progress value={mp.percentage} className="mt-2 h-1.5" />
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {mp.completed}/{mod.lessons.length} lessons
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Lessons List */}
      <div className="container py-8 md:py-12">
        <div className="mx-auto max-w-3xl space-y-3">
          {mod.lessons.map((lesson, idx) => {
            const complete = isLessonComplete(lesson.id);
            return (
              <Link key={lesson.id} to={`/module/${mod.id}/lesson/${lesson.id}`}>
                <Card
                  className={cn(
                    'group transition-all duration-200 hover:shadow-md hover:-translate-y-0.5',
                    complete && 'border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-950/20'
                  )}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 shrink-0">
                        {complete ? (
                          <CheckCircle2 className="size-5 text-emerald-600 dark:text-emerald-400" />
                        ) : (
                          <Circle className="size-5 text-slate-300 dark:text-slate-600" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-medium text-slate-400 dark:text-slate-500">
                            {lesson.number}
                          </span>
                          <CardTitle className="text-base">
                            {lesson.title}
                          </CardTitle>
                        </div>
                        <CardDescription className="mt-1 line-clamp-2 text-xs leading-relaxed">
                          {lesson.coreClaim}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ModulePage;
